/**
 * credits to wapp: https://git.linecorp.com/WebDev/common/tree/develop/packages/%40linepay/wapp
 */
import { OS } from '@pay-mono-web/webview-manager';
import { normalizedProfile } from '@pay-mono-web/webview-manager/lib/sdk.utils';
import { InterfaceType, isClient } from '../utils/interface.utils';
import { LIFFOptions } from './liff/liff.interface';
import {
  ExecuteParameters,
  ExecuteResponse,
  LoginParams,
  OpenWindowParams,
  SdkApiName,
  SdkApis,
  NormalizedProfile,
} from './sdk.interface';
import WebviewManagerUtils from './webview-manager.utils';

export type WebviewManagerOptions = { liff: LIFFOptions };
type TaskQueue<T extends SdkApiName = any> = {
  resolve: (value: ExecuteResponse<T>) => void;
  reject: (reason?: any) => void;
} & ExecuteParameters<any>;

export default class WebviewManager extends WebviewManagerUtils {
  _module: SdkApis | null = null;
  taskQueue: TaskQueue[] = [];

  install(options: WebviewManagerOptions) {
    if (!this._module) {
      this.getModule()
        .then(module => {
          this._module = new module.default(options);
          this.setBodyClassNames();
          this.taskQueue.forEach(({ name, params, resolve }) => {
            resolve(this.call({ name, params }));
          });
        })
        .catch(e => {
          this.taskQueue.forEach(({ reject }) => {
            reject(`[unknown module ${e}]`);
          });
          throw Error(`[unknown module ${e}]`);
        });
    }
  }

  getModule(): Promise<any> {
    switch (this.interfaceType) {
      case InterfaceType.LIFF:
        return import('./liff');
      case InterfaceType.TALARIA:
        return import('./talaria');
      case InterfaceType.UNKNOWN:
        return import('./liff');
    }
  }

  setBodyClassNames() {
    if (!isClient()) return;
    const classList = document.body.classList;

    if (this.os === OS.IOS) {
      classList.add('ios');
    }
    if (this.os === OS.ANDROID) {
      classList.add('android');
    }
    if (this.os === OS.PC) {
      classList.add('pc');
    }
    if (this.isIAB) {
      classList.add('iab');
    }
    if (this.isLIFF) {
      classList.add('liff');
    }
    if (this.isTalaria) {
      classList.add('talaria');
    }
    if (this.isLINE) {
      classList.add('line');
    }
    if (this.isUPay) {
      classList.add('upay');
    }
  }

  call<T extends SdkApiName>({
    name,
    params,
  }: ExecuteParameters<T>): ExecuteResponse<T> {
    if (!this._module) {
      return new Promise((resolve, reject) => {
        this.taskQueue.push({
          name,
          params,
          resolve,
          reject,
        });
      });
    }

    const func = this._module[name];

    if (!func || typeof func !== 'function') {
      throw Error(`[invalid api ${name} on module ${this.interfaceType}`);
    }

    try {
      // FIXME: how to make TypeScript infer the calling function and its related params correctly ?
      // @ts-ignore
      return func && func.call(this._module, params);
    } catch (e) {
      throw Error(`[call api ${name} error ${e} ]`);
    }
  }

  init() {
    return this.call({ name: SdkApiName.INIT });
  }

  getContext() {
    return this.call({ name: SdkApiName.GET_CONTEXT });
  }

  getAccessToken() {
    return this.call({ name: SdkApiName.GET_ACCESS_TOKEN });
  }

  isInClient() {
    return this.call({ name: SdkApiName.IS_IN_CLIENT });
  }

  openWindow(params: OpenWindowParams) {
    return this.call({
      name: SdkApiName.OPEN_WINDOW,
      params: params,
    });
  }

  closeWindow() {
    return this.call({
      name: SdkApiName.CLOSE_WINDOW,
    });
  }

  isLoggedIn() {
    return this.call({ name: SdkApiName.IS_LOGGED_IN });
  }

  login(params: LoginParams) {
    return this.call({ name: SdkApiName.LOGIN, params: params });
  }

  ready() {
    return this.call({ name: SdkApiName.READY });
  }

  async getProfile(): Promise<NormalizedProfile> {
    const profile = await this.call({ name: SdkApiName.GET_PROFILE });

    return normalizedProfile(profile);
  }

  getSession() {
    return this.call({ name: SdkApiName.GET_SESSION });
  }
}
